import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@fly/core';
import { FlyAccordion } from '@fly/shared-ui';

import { useGetArtifactFilesInfiniteQuery } from '../../../../store/registry.service';
import { FileRow } from '../ArtifactFiles/FileRow';
import { FileSkeleton } from '../ArtifactFiles/FileSkeleton';

const MAX_VISIBLE_FILES = 6;
const FILE_ROW_HEIGHT_PX = 24;
const FILE_ROW_GAP_PX = 4;
const LOAD_MORE_THRESHOLD_ROWS = 3;
const LOAD_MORE_THRESHOLD_PX =
  LOAD_MORE_THRESHOLD_ROWS * (FILE_ROW_HEIGHT_PX + FILE_ROW_GAP_PX);
const MAX_LIST_HEIGHT_PX =
  MAX_VISIBLE_FILES * FILE_ROW_HEIGHT_PX +
  (MAX_VISIBLE_FILES - 1) * FILE_ROW_GAP_PX;

export function DistributionFormFiles({
  artifactName,
  versionName,
  packageType,
}: {
  artifactName: string;
  versionName: string;
  packageType: string;
}) {
  const { t } = useTranslation();
  const [isFilesOpen, setIsFilesOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetArtifactFilesInfiniteQuery(
    {
      name: artifactName,
      packageVersion: versionName,
      packageType,
    },
    { skip: !hasBeenOpened }
  );

  const files = useMemo(
    () => data?.pages.flatMap((page) => page.files) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  const handleScroll = useCallback(() => {
    if (!hasNextPage || isLoading || isFetchingNextPage) return;

    const element = scrollContainerRef.current;
    if (!element) return;

    const { scrollHeight, scrollTop, clientHeight } = element;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsFilesOpen(open);
    if (open) {
      setHasBeenOpened(true);
    }
  }, []);

  if (hasBeenOpened && !isLoading && totalCount === 0) {
    return null;
  }

  const hasFileCount = totalCount > 0;
  const heading = isFilesOpen
    ? hasFileCount
      ? t('app.dashboard.artifacts.distribution_form.files_heading', {
          count: totalCount,
          defaultValue: 'Files ({{count}})',
        })
      : t('app.dashboard.artifacts.distribution_form.files_heading_loading', {
          defaultValue: 'Files',
        })
    : hasFileCount
      ? t('app.dashboard.artifacts.distribution_form.show_files_heading', {
          count: totalCount,
          defaultValue: 'Show files ({{count}})',
        })
      : t('app.dashboard.artifacts.distribution_form.show_files_label', {
          defaultValue: 'Show files',
        });

  return (
    <div data-testid='distribution-form-files'>
      <FlyAccordion
        chevronClosedDirection='right'
        onOpenChange={handleOpenChange}
        trigger={
          <Text
            size='3'
            weight='semibold'
            color='neutral'
            highContrast
            data-testid='distribution-form-files-header'
          >
            {heading}
          </Text>
        }
      >
        {isLoading ? (
          <ul className='ml-7 mt-fly-3 list-none'>
            <FileSkeleton />
          </ul>
        ) : error ? (
          <Text size='2' color='error' weight='medium' className='mt-fly-3'>
            {t('app.common.errors.global_error', {
              defaultValue: 'An error occurred',
            })}
          </Text>
        ) : (
          <ul
            ref={scrollContainerRef}
            className='!ml-7 thin-scroller mt-fly-3 flex list-none flex-col gap-fly-1 overflow-y-auto pr-fly-2'
            style={{ maxHeight: `${MAX_LIST_HEIGHT_PX}px` }}
            onScroll={handleScroll}
          >
            {files.map((file, index) => (
              <li
                key={file.sha256 ?? `${file.fileName}-${index}`}
                className='list-none'
              >
                <FileRow file={file} />
              </li>
            ))}
            {isFetchingNextPage && (
              <li className='list-none'>
                <FileSkeleton />
              </li>
            )}
          </ul>
        )}
      </FlyAccordion>
    </div>
  );
}
